import {tasksApi, TaskParentView} from "@dv5/api"
import {CanvasIcon, ErrorDisplayProcessor, LayoutChooserList, layoutStylesService, MarkdownViewer, ViewLinkIcon} from "@dv5/components"
import {
    AppMode,
    chooseLayout,
    DvLayout,
    DvTask,
    getFirstLayout,
    getLeafLayouts,
    getRootLayout,
    getSelectedLayout,
    LayoutContentType,
    TagFiltered,
    useAppDispatch,
    useAppSelector,
} from '@dv5/domain'
import React, {FC, useEffect, useState} from "react"
import {taskApi} from "./services/taskService"
import './TaskView.less'

interface Props {
    task: DvTask
    zoom: number
    mode: AppMode
    layoutId: string
    filteredTags: Array<TagFiltered>
    onNavigateToParent: (contentType: LayoutContentType, contentId: string, layoutId: string) => void
}

const parentTypeToContentType = (type: TaskParentView['type']): LayoutContentType =>
    type === 'CANVAS' ? LayoutContentType.CANVAS : LayoutContentType.VIEW

export const TaskView: FC<Props> = ({
                                        task,
                                        zoom,
                                        mode,
                                        layoutId,
                                        filteredTags,
                                        onNavigateToParent,
                                    }) => {
    const dispatch = useAppDispatch()
    const styles = layoutStylesService.getLayoutStyles(zoom)
    const [loaded, setLoaded] = React.useState(false)
    const [parents, setParents] = useState<Array<TaskParentView>>([])
    const [parentsLoaded, setParentsLoaded] = useState(false)
    const leafLayouts = useAppSelector(getLeafLayouts)
    const selectedLayout = useAppSelector(getSelectedLayout)
    const rootLayout = useAppSelector(getRootLayout)

    useEffect(() => {
        if (!loaded) {
            const tagIds = filteredTags.filter(tag => tag.checked).map(tag => tag.tag.id)
            taskApi.getTask(task.id, [], tagIds)
            setLoaded(true)
        }
    }, [loaded, filteredTags])

    useEffect(() => {
        setLoaded(false)
    }, [filteredTags])

    useEffect(() => {
        setParentsLoaded(false)
        const errorProcessor = new ErrorDisplayProcessor(dispatch)
        tasksApi.getTaskParents(task.id, errorProcessor)
            .then(response => {
                setParents(response.parents || [])
                setParentsLoaded(true)
            })
            .catch(reason => {
                console.error(reason)
                setParentsLoaded(true)
            })
    }, [task.id, dispatch])

    const handleChooseLayout = (layout: DvLayout) => {
        dispatch(chooseLayout(layout))
    }

    const handleNavigateToParent = (parent: TaskParentView) => {
        const targetLayout = selectedLayout ?? (rootLayout ? getFirstLayout(rootLayout) : undefined)
        if (!targetLayout) {
            return
        }
        onNavigateToParent(parentTypeToContentType(parent.type), parent.id, targetLayout.id)
    }

    return (
        <div className='dvLayoutContent' style={styles}>
            <div className='taskViewContainer'>
                <div className='taskCode'>{task.code}</div>
                <div className='taskTitle'>{task.title}</div>
                {task.description?.trim() && (
                    <div className='taskDescription'>
                        <MarkdownViewer sourceCode={task.description} compactMode={false} onToggleCheckBox={() => {}}/>
                    </div>
                )}
                <div className='taskParentViews'>
                    <div className='taskParentViewsTitle'>Parent views</div>
                    {parentsLoaded && parents.length === 0 && (
                        <div className='taskParentViewsEmpty'>Task is not placed in any view or canvas</div>
                    )}
                    {parents.map(parent => (
                        <button
                            key={`${parent.type}_${parent.id}`}
                            className='taskParentViewItem'
                            type='button'
                            title={parent.title}
                            aria-label={`Open ${parent.title}`}
                            onClick={() => handleNavigateToParent(parent)}
                        >
                            <span className='taskParentViewItemIcon'>
                                {parent.type === 'CANVAS' ? <CanvasIcon/> : <ViewLinkIcon/>}
                            </span>
                            <span className='taskParentViewItemTitle'>{parent.title}</span>
                        </button>
                    ))}
                </div>
                {leafLayouts.length > 0 && (
                    <div className='taskParentViewsFooter'>
                        <div className='taskParentViewsDivider'/>
                        <LayoutChooserList
                            layouts={leafLayouts}
                            selectedLayoutId={selectedLayout?.id}
                            onSelect={handleChooseLayout}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
